import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import PageHelmet from '../../components/seo/PageHelmet';
import Layout from '../../components/ui/Layout';
import { SITE_URL, getProductShareUrl, getProductShareSlug } from '../../constants/site';
import ShareButton from '../../components/ui/ShareButton';
import { db } from '../../firebase';
import { collection, query, where, getDocs, limit, doc, getDoc } from 'firebase/firestore'; // Removed orderBy as it might not be used or requires specific indexes with array-contains
import { Container, Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import WelcomeSearchForm from '../../components/public/WelcomeSearchForm';
import { getStorage, ref as storageFBRef, getDownloadURL } from 'firebase/storage';
import { createLogger } from '../../utils/logger';

const logger = createLogger('SearchResultsPage');

const ResultImage = ({ imagePath, altText, type }) => {
  const [imageUrl, setImageUrl] = useState('');
  const storage = getStorage();
  useEffect(() => {
    setImageUrl('');
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:') && !imagePath.startsWith('http')) {
      const imageRef = storageFBRef(storage, imagePath);
      getDownloadURL(imageRef).then(setImageUrl).catch(err => logger.error(`Error fetching ${type} image URL:`, err));
    } else if (imagePath && imagePath.startsWith('http')) {
        setImageUrl(imagePath);
    } else {
        logger.warn("Invalid imagePath for ResultImage:", imagePath);
    }
  }, [imagePath, storage, type]);

  if (!imageUrl) return <div className="card-img-top" style={{ height: type === 'product' ? '180px' : '200px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span>No Image</span></div>;
  return <img src={imageUrl} className="card-img-top" alt={altText} style={{ height: type === 'product' ? '180px' : '200px', objectFit: 'cover' }} />;
};

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('q');

  const [supermarketResults, setSupermarketResults] = useState([]);
  const [productResults, setProductResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productSupermarkets, setProductSupermarkets] = useState({}); // productId -> [supermarkets]
  const [searchRetryNonce, setSearchRetryNonce] = useState(0);

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { label: searchQuery ? `Search: "${searchQuery}"` : "Search Results" }
  ];

  const handleSearchFormSubmit = (trimmedQuery) => {
    const current = (searchQuery ?? '').trim();
    if (current === trimmedQuery) {
      setSearchRetryNonce((n) => n + 1);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const runSearch = async () => {
      if (!searchQuery || searchQuery.trim() === '') {
        if (!cancelled) {
          setLoading(false);
          setSupermarketResults([]);
          setProductResults([]);
          setError('Please enter a search term.');
        }
        return;
      }

      if (!cancelled) {
        setLoading(true);
        setError(null);
      }

      try {
        const trimmedQuery = searchQuery.trim();
        const searchTermProcessed = trimmedQuery.toLowerCase();
        const searchTermsArray = searchTermProcessed.split(/\s+/).filter(term => term.length > 0);

        const supermarketQuery = query(
          collection(db, 'Supermarkets'),
          where('display_name', '>=', searchQuery),
          where('display_name', '<=', `${searchQuery  }\uf8ff`),
          limit(10)
        );

        const productQueries = [];
        if (searchTermsArray.length > 0) {
          productQueries.push(getDocs(query(
            collection(db, 'Products'),
            where('search_keywords', 'array-contains-any', searchTermsArray.slice(0, 10)),
            limit(20)
          )));
        }
        if (/^\d+$/.test(trimmedQuery) && trimmedQuery.length >= 3) {
          productQueries.push(getDocs(query(
            collection(db, 'Products'),
            where('upc', '>=', trimmedQuery),
            where('upc', '<=', `${trimmedQuery}\uf8ff`),
            limit(20)
          )));
        }

        const [supermarketSnap, ...productSnaps] = await Promise.all([
          getDocs(supermarketQuery),
          ...(productQueries.length > 0 ? productQueries : [Promise.resolve({ docs: [] })]),
        ]);

        if (cancelled) {
          return;
        }

        let supermarkets = supermarketSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        supermarkets = supermarkets.filter(s =>
          s.display_name?.toLowerCase().includes(searchTermProcessed)
        );
        setSupermarketResults(supermarkets);

        const productMap = new Map();
        productSnaps.forEach((snap) => {
          snap.docs.forEach((productDoc) => {
            productMap.set(productDoc.id, { id: productDoc.id, ...productDoc.data() });
          });
        });
        const products = Array.from(productMap.values());
        setProductResults(products);

        if (supermarkets.length === 0 && products.length === 0) {
          setError(`No results found for "${searchQuery}".`);
        }
      } catch (err) {
        if (!cancelled) {
          logger.error("Error performing search:", err);
          setError(`Failed to perform search. ${  err.message}`);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    runSearch();

    return () => {
      cancelled = true;
    };
  }, [searchQuery, searchRetryNonce]);

  // Fetch supermarkets for each product result
  useEffect(() => {
    let cancelled = false;

    const fetchSupermarketsForProducts = async () => {
      if (productResults.length === 0) {
        if (!cancelled) {
          setProductSupermarkets({});
        }
        return;
      }
      const result = {};
      await Promise.all(productResults.map(async (product) => {
        const inventoryRef = collection(db, 'Inventory');
        const inventoryQuery = query(
          inventoryRef,
          where('product_id', '==', product.id),
          where('quantity', '>', 0),
          limit(2)
        );
        const inventorySnapshot = await getDocs(inventoryQuery);
        if (cancelled) {
          return;
        }
        const supermarketIds = inventorySnapshot.docs.map(docSnap => docSnap.data().supermarket_id);
        let supermarketsForProduct = [];
        if (supermarketIds.length > 0) {
          const supermarketDocs = await Promise.all(
            supermarketIds.map(id => getDoc(doc(db, 'Supermarkets', id)))
          );
          if (cancelled) {
            return;
          }
          supermarketsForProduct = supermarketDocs
            .filter(docSnap => docSnap.exists())
            .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        }
        result[product.id] = supermarketsForProduct;
      }));
      if (!cancelled) {
        setProductSupermarkets(result);
      }
    };
    fetchSupermarketsForProducts();

    return () => {
      cancelled = true;
    };
  }, [productResults]);

  const seoTitle = searchQuery ? `Search: "${searchQuery}" | Pack My Cart` : 'Search | Pack My Cart';
  const seoDescription = searchQuery
    ? `Search results for "${searchQuery}" - Find products and supermarkets on Pack My Cart.`
    : 'Search for products and supermarkets on Pack My Cart.';

  return (
    <Layout>
      <PageHelmet
        title={seoTitle}
        description={seoDescription}
        path="/search"
        robots={searchQuery ? 'noindex, follow' : 'index, follow'}
      />
      <Container className="mt-4">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
                    &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        <WelcomeSearchForm
          initialQuery={searchQuery ?? ''}
          onSearchSubmit={handleSearchFormSubmit}
          rowClassName="justify-content-center mb-3"
        />
        <h1 className="mb-3 mt-3">Search Results {searchQuery && <small className="text-muted">for "{searchQuery}"</small>}</h1>

        {loading && (
          <div className="text-center my-4">
            <Spinner animation="border" />
            <p className="mb-0 mt-2">Searching...</p>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && supermarketResults.length === 0 && productResults.length === 0 && !error && searchQuery && (
          <Alert variant="info">No results found for "{searchQuery}". Try a different search term.</Alert>
        )}

        {!loading && supermarketResults.length > 0 && (
          <section className="mb-5">
            <h2>Matching Supermarkets ({supermarketResults.length})</h2>
            <Row xs={1} md={2} lg={3} className="g-4">
              {supermarketResults.map(s => (
                <Col key={s.id}>
                  <Card className="h-100 shadow-sm">
                    <Link to={`/supermarkets/${s.id}/products`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      {s.media && s.media.length > 0 && s.media[0].url ? (
                        <ResultImage imagePath={s.media[0].url} altText={s.display_name} type="supermarket" />
                      ) : (
                        <div className="card-img-top" style={{height: '200px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><span>No Image</span></div>
                      )}
                      <Card.Body>
                        <Card.Title>{s.display_name}</Card.Title>
                        {s.address && <Card.Text className="small text-muted">{s.address}</Card.Text>}
                      </Card.Body>
                    </Link>
                    <Card.Footer className="bg-transparent border-top-0 pt-0">
                      <ShareButton
                        url={s.seo_name ? `${SITE_URL}/supermarkets/${s.id}/products/${s.seo_name}` : `${SITE_URL}/supermarkets/${s.id}/products`}
                        title={`${s.display_name} - Products | Pack My Cart`}
                        text={`Shop groceries from ${s.display_name}`}
                        variant="outline-secondary"
                        size="sm"
                        showLabel={false}
                      />
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>
        )}

        {!loading && productResults.length > 0 && (
          <section>
            <h2>Matching Products ({productResults.length})</h2>
            <Row xs={1} sm={2} md={3} lg={4} className="g-4">
              {productResults.map(p => (
                <Col key={p.id}>
                  <Card className="h-100 shadow-sm">
                    {/* Product card content */}
                    {p.media && p.media.length > 0 && p.media[0].url ? (
                        <ResultImage imagePath={p.media[0].url} altText={p.display_name} type="product" />
                    ) : (
                        <div className="card-img-top" style={{height: '180px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><span>No Image</span></div>
                    )}
                    <Card.Body>
                        <Card.Title style={{fontSize: '1rem'}}>{p.display_name}</Card.Title>
                        {/* Display tags if available */}
                        {p.tags && Array.isArray(p.tags) && p.tags.length > 0 && 
                            <p className="small text-muted mb-1">Tags: {p.tags.join(', ')}</p>}
                        {/* Available at supermarkets */}
                        <div className="mt-2">
                          {productSupermarkets[p.id] && productSupermarkets[p.id].length > 0 ? (
                            <>
                              <div className="mb-1" style={{fontSize: '0.95em'}}>Available at:</div>
                              {productSupermarkets[p.id].slice(0,2).map(sup => (
                                <div key={sup.id}>
                                  <Link
                                    to={`/supermarkets/${sup.id}/products/${getProductShareSlug(p)}`}
                                    style={{textDecoration: 'underline', color: '#1a7f37'}}
                                  >
                                    {sup.display_name || 'Supermarket'}
                                  </Link>
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="text-muted" style={{fontSize: '0.95em'}}>Not available in supermarkets</div>
                          )}
                        </div>
                    </Card.Body>
                    <Card.Footer className="bg-transparent border-top-0 pt-0">
                      <ShareButton
                        url={getProductShareUrl(p)}
                        title={`${p.display_name} | Pack My Cart`}
                        text={`Check out ${p.display_name} on Pack My Cart`}
                        variant="outline-secondary"
                        size="sm"
                        showLabel={false}
                      />
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>
        )}
      </Container>
    </Layout>
  );
}
